import ServiceNoteButton from '../driver/components/ServiceNoteButton';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { Platform, View, RefreshControl } from 'react-native';
import { Alert } from '../../components/other/Alert';
import Toast from 'react-native-root-toast';
import React, { useCallback, useMemo, useState } from 'react';
import { compose } from 'redux';
import { useMutation } from '@apollo/client';

import { getExpiryDatesWarningText } from '../../../common/utils/serviceDocket/helpers';
import useConfirmStockLevels from '../../../common/utils/serviceDocket/useConfirmStockLevels';
import { nativePlanogramVersions } from '../../../common/utils/vending-settings';
import { useUserContext } from '../../../common/utils/context';
import { useBooleanState } from '../../../common/utils/hooks';

import { Dialog, ErrorCallout, Text, Heading, WarningDialog } from '../../components';
import { Card, PrimaryButton, Screen, StatusBadge } from '../../design/components';
import { ConfirmStockLevelsContext } from '../../utils/context';
import { defaultConfirmStockLevelsSort, sortPickListItems } from '../../utils/sortMethods';
import { DetailContainer } from '../../containers';
import { useRefetchOnFocus } from '../../hooks';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useOfflineMutation, useOfflineQueue } from '../../offline';

import PlanogramItemExpiryDatePicker from '../planogram/PlanogramItemExpiryDatePicker';
import ReportIncorrectStockSettingDialog from '../planogram/ReportIncorrectStockSettingDialog';
import PlanogramSettingsButton from '../planogram/PlanogramSettingsButton';
import { PlanogramDetail, PlanogramItemEdit } from '../planogram';
import StockReductionReasonDialog from './StockReductionReasonDialog';
import { isMyDayTraining } from '../myDay/myDayTraining';

const getInventoryCheckTone = (inventoryCheck) => {
	if (inventoryCheck === 'Confirmed' || inventoryCheck === 'Confirmed (changes)') {
		return 'success';
	}
	if (inventoryCheck === 'Not confirmed') {
		return 'warning';
	}
	if (inventoryCheck === 'Pending') {
		return 'danger';
	}
	return 'neutral';
};

const InventoryCheckStatus = ({ inventoryCheck, pendingSync }) => {
	const styles = useThemedStyles(createStyles);

	return (
		<View style={styles.status}>
			<Text style={styles.statusLabel}>Confirmation status</Text>
			<View style={styles.statusBadges}>
				{pendingSync && (
					<StatusBadge label="Sync pending" tone="warning" size="small" />
				)}
				{Boolean(inventoryCheck) && (
					<StatusBadge label={inventoryCheck} tone={getInventoryCheckTone(inventoryCheck)} size="small" />
				)}
			</View>
		</View>
	);
};

const ConfirmStockLevels = ({
	data: docket,
	id: docketId,
	refetch,
	setAnyUnsavedChanges,
	setConfirmStockLevels,
	anyUnsavedChanges,
	restockCorrectionStockReviewRevision,
	trainingContext,
	backLabel,
	onConfirmed,
	onCancelConfirmation,
	banner,
	nextStepLabel,
}) => {
	const styles = useThemedStyles(createStyles);
	const [isSettingsDialogOpen, toggleSettingsDialog] = useBooleanState(false);
	const { user, tenant, tenantSettings } = useUserContext();
	const { isOnline, queue } = useOfflineQueue();
	const isTraining = isMyDayTraining(trainingContext);
	const scopedQueue = useMemo(() => (isTraining ? [] : queue), [isTraining, queue]);
	const isCorrectionStockReview = restockCorrectionStockReviewRevision !== undefined &&
		restockCorrectionStockReviewRevision !== null;
	const stockReviewAvailability = docket.marketRestockStockReviewAvailability;
	const stockReviewRevisionMatches = Number(stockReviewAvailability?.correctionRevision) ===
		Number(restockCorrectionStockReviewRevision) &&
		Number(docket.restockRevision || 0) === Number(restockCorrectionStockReviewRevision);
	const pendingMachineInventoryOperation = useMemo(() => (scopedQueue || []).some(entry => (
		['manualRestock', 'confirmStockLevels', 'confirmMachineValues'].includes(entry.configKey) &&
		(
			entry.meta?.machineId === docket.machine?.id ||
			entry.meta?.serviceDocketId === docketId
		)
	)), [docket.machine?.id, docketId, scopedQueue]);
	const stockReviewAllowed = !isCorrectionStockReview || (
		stockReviewAvailability?.allowed &&
		stockReviewRevisionMatches &&
		isOnline &&
		!pendingMachineInventoryOperation
	);
	const stockReviewError = !stockReviewRevisionMatches
		? 'A newer delivery correction is already recorded. Go back and start again from the latest service.'
		: !stockReviewAvailability?.allowed
			? stockReviewAvailability?.reason
			: !isOnline
				? 'Reconnect before reviewing and confirming stock levels.'
				: pendingMachineInventoryOperation
					? 'Sync the pending restock or stock confirmation before reviewing stock levels.'
					: null;

	const { planogramItemDaysSinceLastSale } = tenantSettings;
	const [reportTargetId, setReportTargetId] = useState(null);

	const {
		confirmStockLevelsItems,
		loading,
		onClickPlanogramItem,
		confirmStockLevelsContextValues,
		machineUsesIngredients,
		isMarket,
		machineName,
		isRefreshing,
		setIsRefreshing,
		editedPickListItemId,
		setEditedPickListItemId,
		expiryDateItemKey,
		setExpiryDateItemKey,
		onClickExpiryDateButton,
		inventoryCheck,
		onPressConfirmStockLevels,
		changeStockItem,
		showExpiryDatesDialogWarning,
		setShowExpiryDatesDialogWarning,
		openEditDialog,
		setOpenEditDialog,
		blockConfirmAnyway,
		setExpiryDateSelectorDate,
		expiryDateSelectorDate,
		productNamesToFillExpiryDates,
		pendingStockReductionReasons,
		resolveStockReductionReasons,
		skipStockReductionReasons,
		reportIncorrectStockSetting,
		isStockSettingReported,
		reportingIncorrectStockSetting,
	} = useConfirmStockLevels({
		docket,
		docketId,
		setAnyUnsavedChanges,
		setConfirmStockLevels,
		tenantSettings,
		timezone: tenant?.timezone,
		mutationHook: isCorrectionStockReview ? useMutation : useOfflineMutation,
		mutationHookOptions: isCorrectionStockReview ? undefined : {
			configKey: 'confirmStockLevels',
			disableOffline: isTraining,
		},
		reportMutationHook: useOfflineMutation,
		reportMutationHookOptions: {
			configKey: 'reportIncorrectStockSetting',
			disableOffline: isTraining,
		},
		onReportIncorrectStockSetting: useCallback(itemId => setReportTargetId(itemId), []),
		refetch,
		enableStockReductionReasonPrompt: Boolean(tenantSettings.enableStockReductionReasonPrompt),
		restockCorrectionStockReviewRevision,
		confirmationBlockedMessage: isCorrectionStockReview && !stockReviewAllowed
			? stockReviewError || 'Stock cannot be safely reconfirmed from this service.'
			: null,
		adminTraining: isTraining,
	});
	const reportTarget = reportTargetId
		? confirmStockLevelsItems.find(item => item.id === reportTargetId)
		: null;
	const closeReportDialog = useCallback(() => setReportTargetId(null), []);
	const submitReportIncorrectSetting = useCallback(async ({ setting, suggestedValue, detail }) => {
		const result = await reportIncorrectStockSetting({
			itemId: reportTargetId,
			setting,
			suggestedValue,
			detail,
		});
		setReportTargetId(null);
		if (result?.offline) {
			Toast.show('Report queued; your manager will be notified when back online');
		}
		else {
			Toast.show('Your manager has been notified');
		}
	}, [reportIncorrectStockSetting, reportTargetId]);
	const hasPendingInventorySync = useMemo(() => (scopedQueue || []).some(entry => entry.configKey === 'confirmStockLevels' && entry.meta?.serviceDocketId === docketId), [scopedQueue, docketId]);
	useRefetchOnFocus(refetch, {
		enabled: !anyUnsavedChanges &&
			!loading &&
			!hasPendingInventorySync &&
			!openEditDialog &&
			!expiryDateItemKey &&
			!showExpiryDatesDialogWarning &&
			!pendingStockReductionReasons.length,
	});

	const onRefresh = useCallback(() => {
		Alert.alert(
			'Refresh stock levels',
			'Warning! This will get the latest stock levels and overwrite any values you have already adjusted. Do you want to proceed?',
			[
				{ text: 'Cancel', onPress: () => setIsRefreshing(false), style: 'cancel' },
				{
					text: 'Proceed',
					onPress: () => {
						setIsRefreshing(true);
					},
				},
			],
			{ cancelable: false },
		);
	}, [setIsRefreshing]);



	const {
		settings: {
			confirmStockLevelsSort,
			confirmStockLevelsHideImages,
			confirmStockLevelsHideHealthCompliance,
			confirmStockLevelsShowSalesLastXDays,
			confirmStockLevelsShowExpiryDateButtons,
		},
	} = user;

	// Native iOS cannot show two modals at once, so the product card closes while the expiry
	// date picker is open and reopens after it. Web stacks the picker over the card instead,
	// so the card does not vanish and reappear around every date tap.
	const isEditDialogOpen = Boolean(editedPickListItemId && openEditDialog && (Platform.OS === 'web' || !expiryDateItemKey));

	const confirmLabel = isCorrectionStockReview ? 'Confirm reviewed stock levels' : 'Confirm stock levels';
	const onPressConfirm = async () => {
		try {
			const result = await onPressConfirmStockLevels();
			if (result?.confirmationAborted) {
				return;
			}
			if (result?.offline) {
				Toast.show('Confirmation queued; will sync when back online');
			}
			const { showExpiryDatesDialogWarning } = result || {};
			if (!showExpiryDatesDialogWarning) {
				onConfirmed();
			}
		}
		catch (e) {
			Toast.show(e?.message || 'Error when confirming stock levels');
		}
	};

	return (
		<View style={styles.container}>
			<Screen
				scroll={false}
				backLabel={backLabel}
				banner={banner}
				headerRight={(
					<View style={styles.headerActions}>
						<ServiceNoteButton />
						<PlanogramSettingsButton onPress={toggleSettingsDialog} accessibilityLabel="Stock level settings" />
					</View>
				)}
			>
				{ loading && (
					<View
						style={styles.loadingOverlay}
					/>
				)}
				<KeyboardAwareScrollView
					// "enableResetScrollToCoords" - we need it only in ingredient machines (setting quantities)
					// it breaks normal ones scrolling to the top when closing planogram edit dialog
					// adding this condition for now
					enableResetScrollToCoords={machineUsesIngredients}
					scrollIndicatorInsets={{ right: 1 }}
					refreshControl={(
						<RefreshControl
							refreshing={isRefreshing}
							onRefresh={onRefresh}
						/>
					)}
				>
					<View style={styles.top}>
						<Heading
							heading={isCorrectionStockReview ? 'Review stock levels' : 'Confirm stock levels'}
							subHeading={machineName}
						/>
						{isCorrectionStockReview && (
							<Card tone="warning" style={styles.stockReviewNotice}>
								<Text style={styles.stockReviewNoticeTitle}>Review the actual physical stock in the market.</Text>
								<Text style={styles.stockReviewNoticeText}>
									The latest delivery correction is loaded, and each value starts from the confirmed count
									that was left unchanged. Enter or verify the quantity physically present; corrected delivered
									quantities are never copied into these counts automatically.
								</Text>
							</Card>
						)}
						{isCorrectionStockReview && !stockReviewAllowed && (
							<ErrorCallout text={stockReviewError || (
								'Stock cannot be safely reconfirmed from this service. Arrange a controlled stock review instead.'
							)} />
						)}
						<InventoryCheckStatus inventoryCheck={inventoryCheck} pendingSync={hasPendingInventorySync} />
					</View>
					<View style={styles.list}>
						<ConfirmStockLevelsContext.Provider
							value={confirmStockLevelsContextValues}
						>
							<PlanogramDetail
								items={confirmStockLevelsItems}
								isConfirmStockLevels={true}
								version={nativePlanogramVersions.CONFIRM_STOCK_LEVELS_VIEW}
								onClickItem={onClickPlanogramItem}
								// Default planogram filters
								defaultSortMethod={confirmStockLevelsSort || defaultConfirmStockLevelsSort}
								sortFunction={sortPickListItems}
								defaultHideImages={confirmStockLevelsHideImages}
								defaultHideHealthCompliance={confirmStockLevelsHideHealthCompliance}
								machineUsesIngredients={machineUsesIngredients}
								defaultShowAdvancedView={true}
								defaultShowSalesLastXDays={confirmStockLevelsShowSalesLastXDays}
								defaultShowExpiryDateButtons={confirmStockLevelsShowExpiryDateButtons}
								isMarket={isMarket}
								planogramItemGroups={docket.machine.planogram.planogramItemGroups}
								isSettingsDialogOpen={isSettingsDialogOpen}
								toggleSettingsDialog={toggleSettingsDialog}
							/>
						</ConfirmStockLevelsContext.Provider>
					</View>
					{/* After the last product, not pinned, so drivers scroll through every product before confirming. */}
					<View style={styles.footer}>
						{Boolean(nextStepLabel) && <Text style={styles.footerHint}>After this: {nextStepLabel}</Text>}
						<PrimaryButton
							label={confirmLabel}
							disabled={loading || !stockReviewAllowed || (isTraining && !isOnline) || Boolean(pendingStockReductionReasons.length)}
							loading={loading}
							onPress={onPressConfirm}
						/>
					</View>
				</KeyboardAwareScrollView>
			</Screen>
			<Dialog
				isOpen={isEditDialogOpen}
				onClose={() => {
					setEditedPickListItemId(null);
					setOpenEditDialog(false);
				}}
			>
				{
					isEditDialogOpen && (
						<ConfirmStockLevelsContext.Provider
							value={confirmStockLevelsContextValues}
						>
							<PlanogramItemEdit
								isMarket={isMarket}
								usesDynamicParlevels={docket.machine.usesDynamicParlevels}
								planogramItem={confirmStockLevelsItems.find(i => i.id === editedPickListItemId)}
								setOpenEditDialog={setOpenEditDialog}
								setPlanogramItemValue={changeStockItem}
								enablePlanogramItemCapacity={tenantSettings.enablePlanogramItemCapacity}
								planogramItemDaysSinceLastSale={planogramItemDaysSinceLastSale}
								defaultShowSalesLastXDays={confirmStockLevelsShowSalesLastXDays}
								pos={docket.machine}
							/>
						</ConfirmStockLevelsContext.Provider>
					)
				}
			</Dialog>
			{
				(expiryDateItemKey && editedPickListItemId && tenantSettings.useExpiryDates) && (
					<PlanogramItemExpiryDatePicker
						isMarket={isMarket}
						onClickExpiryDateButton={onClickExpiryDateButton}
						usesDynamicParlevels={docket.machine.usesDynamicParlevels}
						planogramItem={confirmStockLevelsItems.find(i => i.id === editedPickListItemId)}
						setPlanogramItemValue={changeStockItem}
						enablePlanogramItemCapacity={tenantSettings.enablePlanogramItemCapacity}
						planogramItemDaysSinceLastSale={planogramItemDaysSinceLastSale}
						defaultShowSalesLastXDays={confirmStockLevelsShowSalesLastXDays}
						pos={docket.machine}
						expiryDateItemKey={expiryDateItemKey}
						setExpiryDateItemKey={setExpiryDateItemKey}
						setEditedPickListItemId={setEditedPickListItemId}
						setExpiryDateSelectorDate={setExpiryDateSelectorDate}
						expiryDateSelectorDate={expiryDateSelectorDate}
					/>
				)
			}
			<ReportIncorrectStockSettingDialog
				isOpen={Boolean(reportTargetId && reportTarget)}
				planogramItem={reportTarget}
				showCapacity={Boolean(tenantSettings.enablePlanogramItemCapacity)}
				reportedSettings={{
					PARLEVEL: isStockSettingReported(reportTargetId, 'PARLEVEL'),
					CAPACITY: isStockSettingReported(reportTargetId, 'CAPACITY'),
				}}
				submitting={reportingIncorrectStockSetting}
				onClose={closeReportDialog}
				onSubmit={submitReportIncorrectSetting}
			/>
			<WarningDialog
				title="Expiry dates"
				texts={[
					'Please update the expiry dates to confirm stock levels.',
				].filter(Boolean)}
				// show names of products with no date filled
				subtext={getExpiryDatesWarningText(productNamesToFillExpiryDates, isMarket)}
				hideLeftButton={blockConfirmAnyway}
				leftButtonText="Confirm anyway"
				setIsOpen={isOpen => {
					setShowExpiryDatesDialogWarning(isOpen);
					if (!isOpen) {
						onCancelConfirmation();
					}
				}}
				isOpen={showExpiryDatesDialogWarning}
				enableCloseDialog={true}
				onClick={async () => {
					try {
						const result = await onPressConfirmStockLevels(true);
						if (!result?.confirmationAborted) {
							onConfirmed();
						}
					}
					catch (error) {
						Toast.show(error?.message || 'Error when confirming stock levels');
					}
				}}
			/>
			<StockReductionReasonDialog
				requests={pendingStockReductionReasons}
				onSave={resolveStockReductionReasons}
				onSkip={skipStockReductionReasons}
			/>
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	headerActions: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.xs,
	},
	container: {
		backgroundColor: colors.canvas,
		flex: 1,
	},
	loadingOverlay: {
		position: 'absolute',
		backgroundColor: colors.canvas,
		opacity: 0.6,
		top: 0,
		bottom: 0,
		left: 0,
		right: 0,
		zIndex: 999,
	},
	top: {
		paddingHorizontal: space.gutter,
		gap: space.md,
	},
	list: {
		paddingBottom: space.lg,
	},
	footer: {
		paddingHorizontal: space.gutter,
		paddingBottom: space.xl,
		gap: 10,
	},
	footerHint: {
		...type.caption,
		color: colors.textSecondary,
	},
	status: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: space.sm,
		paddingBottom: space.xs,
	},
	statusLabel: {
		...type.overline,
		color: colors.textSecondary,
	},
	statusBadges: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		justifyContent: 'flex-end',
		gap: space.xs,
	},
	stockReviewNotice: {
		gap: space.xs,
	},
	stockReviewNoticeTitle: {
		...type.bodySmallSemibold,
		color: colors.text,
	},
	stockReviewNoticeText: {
		...type.bodySmall,
		color: colors.textSecondary,
	},
});

export default compose(DetailContainer)(ConfirmStockLevels);
